const MainSearch = class extends HTMLElement {
  constructor() {
    super();

    // Clicking close button
    this.querySelectorAll('.main-search__close').forEach((el) => {
      el.addEventListener('click', (evt) => {
        evt.preventDefault();
        document.body.classList.remove('show-search');
        this.style.height = '';
        this.style.maxHeight = '';
        if (typeof theme.setHeaderSearchExpanded === 'function') {
          theme.setHeaderSearchExpanded(false);
        }
      });
    });

    // Keep the panel inside the visible screen when the phone keyboard is open.
    const fitToKeyboard = () => {
      const viewport = window.visualViewport;
      const open = document.body.classList.contains('show-search');
      if (!open || !viewport || !window.matchMedia('(max-width: 767.98px)').matches) {
        this.style.height = '';
        this.style.maxHeight = '';
        return;
      }
      const height = Math.round(viewport.height);
      this.style.height = height + 'px';
      this.style.maxHeight = height + 'px';
    };
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', fitToKeyboard);
      window.visualViewport.addEventListener('scroll', fitToKeyboard);
    }
    document.addEventListener('click', (evt) => {
      if (evt.target.closest && evt.target.closest('.show-search-link')) {
        window.setTimeout(fitToKeyboard, 60);
      }
    });

    // Pressing escape key
    this.querySelector('.main-search__input').addEventListener('keyup', (evt) => {
      if (evt.key === 'Escape') {
        this.querySelector('.main-search__close').dispatchEvent(new Event('click'));
      }
    });

    // Search as you type
    if (this.dataset.quickSearch === 'true') {
      this.initQuickSearch();
    }
  }

  initQuickSearch() {
    const searchInput = this.querySelector('.main-search__input');
    const searchTimeoutThrottle = 500;
    const resultLimit = 8;
    const collectionImageCache = {};
    const includeMeta = this.dataset.quickSearchMeta === 'true';
    let searchTimeoutID = -1;
    let searchAbortController = null;

    const handleInputChange = () => {
      const resultsBox = this.querySelector('.main-search__results');
      const valueToSearch = searchInput.value;

      // Only search if search string longer than 2, and it has changed
      if (valueToSearch.length && valueToSearch !== this.oldSearchValue) {
        // Save previous value
        this.oldSearchValue = valueToSearch;

        // Kill outstanding ajax request
        if (searchAbortController !== null) {
          searchAbortController.abort();
          searchAbortController = null;
        }

        // Kill previous search
        clearTimeout(searchTimeoutID);

        // Create URL for full search results
        const form = searchInput.closest('form');
        const linkURL = new URL(form.action);
        const formParams = new URLSearchParams(new FormData(form));
        formParams.forEach((value, key) => { linkURL.searchParams.set(key, value); });

        // Show loading
        this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--no-results');
        this.classList.add('main-search--loading');
        if (!this.querySelector('.main-search__results-spinner')) {
          resultsBox.innerHTML = '<div class="main-search__results-spinner"><div class="loading-spinner"></div></div>';
        }

        // Do next search (in X milliseconds). Cards come from the local model
        // index so a slow or failed Shopify request cannot leave the spinner up.
        searchTimeoutID = setTimeout(() => {
          const runSearch = (queryText, allowFallback, vinInfo) => {
            if (searchAbortController !== null) {
              searchAbortController.abort();
              searchAbortController = null;
            }

            this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--no-results');
            resultsBox.innerHTML = '';

            const resultsProducts = document.createElement('div');
            resultsProducts.className = 'main-search__results__products collection-listing';
            resultsProducts.innerHTML = '<div class="product-grid product-grid--per-row-4 product-grid--per-row-mob-2"></div>';
            const resultsPages = document.createElement('div');
            resultsPages.className = 'main-search__results__pages';
            const seenTitles = {};

            const showResults = () => {
              if (searchInput.value !== valueToSearch) return;
              this.classList.remove('main-search--loading');
              const areProducts = !!resultsProducts.querySelector('.main-search-result');
              const arePages = !!resultsPages.querySelector('.main-search-result');
              const parsed = window.AwModelSearch ? window.AwModelSearch.parseQuery(queryText) : { years: [] };
              if (!areProducts && allowFallback && parsed.years.length) {
                runSearch(parsed.tokens.join(' '), false, vinInfo);
                return;
              }
              if (vinInfo && vinInfo.label) {
                const note = document.createElement('div');
                note.className = 'main-search__vin-note';
                if (areProducts) {
                  note.textContent = `VIN matches ${vinInfo.label}.`;
                } else {
                  note.textContent = `VIN matches ${vinInfo.label}, but no exact floor mat collection was found.`;
                  const customHref = window.AwModelSearch && window.AwModelSearch.customRequestUrl
                    ? window.AwModelSearch.customRequestUrl(vinInfo, '/')
                    : '/pages/custom-mat-request';
                  const customLink = document.createElement('a');
                  customLink.className = 'main-search__vin-custom-link';
                  customLink.href = customHref;
                  customLink.textContent = 'Request custom floor mats';
                  note.appendChild(document.createElement('br'));
                  note.appendChild(customLink);
                  // Same as scanner / VIN page: send customer to custom request.
                  window.setTimeout(function () {
                    window.location.href = customHref;
                  }, 1200);
                }
                resultsBox.appendChild(note);
              }
              if (areProducts || arePages) {
                this.classList.add('main-search--has-results');
                this.classList.toggle('main-search--results-on-multiple-lines', resultsProducts.querySelectorAll('.product-block, .aw-search-index-hit').length > 4);
                if (areProducts) resultsBox.appendChild(resultsProducts);
                if (arePages) {
                  const heading = document.createElement('h6');
                  heading.className = 'main-search-result__heading';
                  heading.innerHTML = theme.strings.generalSearchPages;
                  resultsPages.insertAdjacentElement('afterbegin', heading);
                  resultsBox.appendChild(resultsPages);
                }
                const allLink = document.createElement('a');
                allLink.className = 'main-search__results-all-link btn btn--secondary';
                const viewAll = new URL(linkURL);
                const productHits = resultsProducts.querySelector('a[href*="/products/"]');
                const collectionHits = resultsProducts.querySelector('a[href*="/collections/"]');
                if (productHits && !collectionHits) {
                  viewAll.searchParams.set('q', queryText);
                  viewAll.searchParams.set('type', 'product');
                } else {
                  if (window.AwModelSearch) viewAll.searchParams.set('q', window.AwModelSearch.shopifyQuery(queryText));
                  viewAll.searchParams.set('type', productHits ? 'product,collection' : 'collection');
                }
                allLink.href = viewAll;
                allLink.innerHTML = theme.strings.generalSearchViewAll;
                resultsBox.appendChild(allLink);
              } else {
                this.classList.add('main-search--no-results');
                const emptyMessage = document.createElement('div');
                emptyMessage.className = 'main-search__empty-message';
                emptyMessage.innerHTML = theme.strings.generalSearchNoResultsWithoutTerms;
                resultsBox.appendChild(emptyMessage);
              }
            };

            const indexNode = document.querySelector('[data-model-index]');
            const indexUrl = indexNode ? indexNode.getAttribute('data-model-index') : (this.getAttribute('data-model-index') || '');
            const collectionImage = (item) => {
              if (collectionImageCache[item.h]) return collectionImageCache[item.h];
              const query = item.t || String(item.h || '').replace(/-/g, ' ');
              const imageUrl = new URL('/search/suggest.json', theme.routes.base);
              imageUrl.searchParams.set('q', query);
              imageUrl.searchParams.set('resources[type]', 'collection');
              imageUrl.searchParams.set('resources[limit]', '10');
              collectionImageCache[item.h] = fetch(imageUrl)
                .then((imageResponse) => (imageResponse.ok ? imageResponse.json() : null))
                .then((data) => {
                  const cols = data && data.resources && data.resources.results && data.resources.results.collections || [];
                  const match = cols.find((col) => col.handle === item.h && col.featured_image && col.featured_image.url);
                  if (!match) return '';
                  return `${match.featured_image.url}${match.featured_image.url.indexOf('?') >= 0 ? '&' : '?'}width=640`;
                })
                .catch(() => '');
              return collectionImageCache[item.h];
            };
            const addTextHit = (item, opts) => {
              const link = document.createElement('a');
              link.className = 'aw-search-index-hit main-search-result';
              link.href = (window.AwModelSearch && window.AwModelSearch.collectionUrl)
                ? window.AwModelSearch.collectionUrl(item.h)
                : ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'collections/' + item.h;
              link.setAttribute('data-model-handle', item.h);
              const escapeText = (value) => String(value || '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
              const brand = opts && opts.hideBrand ? '' : window.AwModelSearch.brandLabel(item.b);
              const title = escapeText(item.t);
              const knownImage = escapeText(item.i);
              link.innerHTML = '<span class="aw-search-index-hit__media img-ar img-ar--contain" style="--aspect-ratio:1">'
                + (knownImage ? '<img src="' + knownImage + '" alt="" width="480" height="480" loading="eager" decoding="async">' : '')
                + '</span><span class="aw-search-index-hit__copy">'
                + (brand ? '<span class="aw-search-origin">' + escapeText(brand) + '</span>' : '')
                + '<span class="product-block__title">' + title + '</span></span>';
              resultsProducts.firstElementChild.appendChild(link);
              if (knownImage) return;
              collectionImage(item).then((src) => {
                const media = link.querySelector('.aw-search-index-hit__media');
                if (!src || !media || !link.isConnected || media.querySelector('img')) return;
                const img = document.createElement('img');
                img.src = src;
                img.alt = '';
                img.width = 480;
                img.height = 480;
                img.loading = 'eager';
                media.appendChild(img);
              });
            };

            const isProductQuery = (query) => {
              const raw = String(query || '').trim();
              const parsed = window.AwModelSearch ? window.AwModelSearch.parseQuery(raw) : { tokens: raw.split(/\s+/) };
              if ((parsed.tokens || []).length >= 4) return true;
              return /^[^ ]{4,}$/.test(raw) && /\d/.test(raw) && /[-_()/]/.test(raw);
            };
            const addProductHit = (product, before) => {
              const link = document.createElement('a');
              link.className = 'aw-search-index-hit main-search-result';
              let href = product.url || ('/products/' + product.handle);
              try {
                const parsed = new URL(href, window.location.origin);
                ['_pos', '_psq', '_psid', '_ss'].forEach((key) => parsed.searchParams.delete(key));
                href = parsed.pathname + parsed.search;
              } catch (e) { /* keep the original url */ }
              link.href = href;
              const escapeText = (value) => String(value || '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
              let src = typeof product.image === 'string' ? product.image : '';
              if (src.indexOf('//') === 0) src = 'https:' + src;
              if (src && src.indexOf('width=') === -1) src += (src.indexOf('?') >= 0 ? '&' : '?') + 'width=480';
              link.innerHTML = '<span class="aw-search-index-hit__media img-ar img-ar--contain" style="--aspect-ratio:1">'
                + (src ? '<img src="' + escapeText(src) + '" alt="" width="480" height="480" loading="eager" decoding="async">' : '')
                + '</span><span class="aw-search-index-hit__copy"><span class="product-block__title">' + escapeText(product.title) + '</span></span>';
              const grid = resultsProducts.firstElementChild;
              if (before && grid.firstChild) grid.insertBefore(link, before);
              else grid.appendChild(link);
            };
            const fetchProducts = () => {
              const productUrl = new URL('/search/suggest.json', theme.routes.base);
              productUrl.searchParams.set('q', queryText);
              productUrl.searchParams.set('resources[type]', 'product');
              productUrl.searchParams.set('resources[limit]', String(resultLimit));
              productUrl.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,variants.sku,vendor');
              productUrl.searchParams.set('resources[options][unavailable_products]', 'last');
              const controller = typeof AbortController === 'function' ? new AbortController() : null;
              const timer = controller ? window.setTimeout(() => controller.abort(), 4000) : null;
              return fetch(productUrl, controller ? { signal: controller.signal } : undefined)
                .then((response) => (response.ok ? response.json() : null))
                .then((data) => (data && data.resources && data.resources.results && data.resources.results.products) || [])
                .catch(() => [])
                .finally(() => { if (timer) window.clearTimeout(timer); });
            };

            const paintIndex = (decoded) => {
              if (!(window.AwModelSearch && window.AwModelSearch.loadIndex && indexUrl)) return Promise.resolve();
              return window.AwModelSearch.loadIndex(indexUrl).then((index) => {
                if (searchInput.value !== valueToSearch) return;
                const hits = decoded
                  ? window.AwModelSearch.searchVehicle(index, decoded, 1)
                  : window.AwModelSearch.searchIndex(index, queryText, resultLimit);
                hits.forEach((item) => {
                  if (resultsProducts.querySelectorAll('.main-search-result').length >= resultLimit) return;
                  const titleKey = String(item.t || '').replace(/\s+/g, ' ').trim().toLowerCase();
                  if (titleKey && seenTitles[titleKey]) return;
                  if (titleKey) seenTitles[titleKey] = true;
                  addTextHit(item, { hideBrand: Boolean(decoded) });
                });
              }).catch(() => {});
            };

            paintIndex(vinInfo || null).then(() => {
              if (searchInput.value !== valueToSearch) return;
              const collectionCount = resultsProducts.querySelectorAll('.main-search-result').length;
              const productQuery = isProductQuery(queryText);
              if ((collectionCount && !productQuery) || vinInfo) {
                showResults();
                return;
              }
              return fetchProducts().then((products) => {
                if (searchInput.value !== valueToSearch) return;
                const anchor = resultsProducts.firstElementChild.firstChild;
                products.slice(0, anchor ? 4 : resultLimit).forEach((product) => {
                  if (!product || !product.title) return;
                  addProductHit(product, productQuery ? anchor : null);
                });
                showResults();
              });
            });
          };

          if (window.AwModelSearch && window.AwModelSearch.looksLikeVin(valueToSearch)) {
            window.AwModelSearch.decodeVin(valueToSearch).then((decoded) => {
              if (searchInput.value !== valueToSearch) return;
              if (!decoded) {
                runSearch(valueToSearch, true);
                return;
              }
              runSearch(decoded.query || valueToSearch, false, decoded);
            });
            return;
          }

          runSearch(valueToSearch, true);
        }, searchTimeoutThrottle);
      } else if (!valueToSearch.length) {
        // Abandon current search
        this.oldSearchValue = valueToSearch;
        if (searchAbortController !== null) {
          searchAbortController.abort();
          searchAbortController = null;
        }
        clearTimeout(searchTimeoutID);

        // Clear results
        this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--loading');
        resultsBox.innerHTML = '';
      }
    };

    searchInput.addEventListener('keyup', handleInputChange.bind(this));
    searchInput.addEventListener('input', handleInputChange.bind(this));
    searchInput.addEventListener('change', handleInputChange.bind(this));
    const searchForm = searchInput.closest('form');
    if (searchForm) {
      searchForm.addEventListener('submit', () => {
        const typeInput = searchForm.querySelector('input[name="type"]');
        const resultsBox = this.querySelector('.main-search__results');
        if (!typeInput || !resultsBox) return;
        const raw = searchInput.value.trim();
        const parsed = window.AwModelSearch ? window.AwModelSearch.parseQuery(raw) : { tokens: raw.split(/\s+/) };
        const productQuery = (parsed.tokens || []).length >= 4 || (/^[^ ]{4,}$/.test(raw) && /\d/.test(raw) && /[-_()/]/.test(raw));
        const productHits = resultsBox.querySelector('a[href*="/products/"]');
        const collectionHits = resultsBox.querySelector('a[href*="/collections/"]');
        if ((productHits && !collectionHits) || (productQuery && !collectionHits)) typeInput.value = 'product';
        else if (productHits && collectionHits) typeInput.value = 'product,collection';
      });
    }
  }
};

window.customElements.define('main-search', MainSearch);
